import produce from 'immer';

import {
  AI_MISMATCH_VALIDATION_RULE_ID,
  TableValidationCellError,
} from 'notebook/structured-tables/StructuredTableApiConstants';
import {ValidationErrorSnapshot} from 'notebook/tables/TableStoreTransforms';
import {Note} from 'notebook/util/NoteUtilConstants';

/**
 * Strip AI mismatch validation errors from a snapshot.
 *
 * - If both ``tableName`` and ``cellId`` are given, only that cell's mismatch entries are removed.
 * - If only ``tableName`` is given, all mismatch entries for that table are removed.
 * - If neither is given, all mismatch entries across the entire snapshot are removed.
 */
export function clearMismatchValidationErrors(
  snapshot: ValidationErrorSnapshot | null | undefined,
  tableName?: string,
  cellId?: {columnProvenanceUuid: string; rowId: string}
): ValidationErrorSnapshot | null {
  if (snapshot == null) {
    return null;
  }

  const updated: ValidationErrorSnapshot = {};
  let changed = false;
  for (const [name, errors] of Object.entries(snapshot)) {
    if (tableName != null && name !== tableName) {
      updated[name] = errors;
      continue;
    }
    const filtered = _stripMismatchErrors(errors, cellId);
    if (filtered === errors) {
      updated[name] = errors;
    } else if (filtered.length > 0) {
      updated[name] = filtered;
      changed = true;
    } else {
      changed = true;
    }
  }
  return changed ? updated : snapshot;
}

/**
 * Apply ``clearMismatchValidationErrors`` to the validation error snapshot nested inside a note.
 * Returns the same note reference if nothing changed.
 *
 * - If both ``tableName`` and ``cellId`` are given, only that cell's mismatch entries are removed.
 * - If only ``tableName`` is given, all mismatch entries for that table are removed.
 * - If neither is given, all mismatch entries across the entire snapshot are removed.
 */
export function clearMismatchValidationErrorsInNote(
  note: Note,
  tableName?: string,
  cellId?: {columnProvenanceUuid: string; rowId: string}
): Note {
  const snapshot = note.noteRuntimeSnapshot?.validationErrorSnapshot;
  const cleared = clearMismatchValidationErrors(snapshot, tableName, cellId);
  if (cleared === snapshot) {
    return note;
  }
  return produce(note, (draft) => {
    if (draft.noteRuntimeSnapshot) {
      draft.noteRuntimeSnapshot.validationErrorSnapshot = cleared;
    }
  });
}

function _stripMismatchErrors(
  errors: Array<TableValidationCellError>,
  cellId?: {columnProvenanceUuid: string; rowId: string}
): Array<TableValidationCellError> {
  let changed = false;
  const result: Array<TableValidationCellError> = [];
  for (const entry of errors) {
    if (
      cellId != null &&
      (entry.rowId !== cellId.rowId || entry.columnProvenanceUuid !== cellId.columnProvenanceUuid)
    ) {
      result.push(entry);
      continue;
    }
    const kept = entry.text.filter((t) => t.validation_rule_id !== AI_MISMATCH_VALIDATION_RULE_ID);
    if (kept.length === 0) {
      changed = true;
    } else if (kept.length === entry.text.length) {
      result.push(entry);
    } else {
      result.push({...entry, text: kept});
      changed = true;
    }
  }
  return changed ? result : errors;
}
